import axios, { type AxiosInstance, type InternalAxiosRequestConfig, type AxiosResponse, AxiosError } from 'axios';
import { Toast } from 'react-vant';
import i18n from '../i18n';
// 定义响应数据的接口
interface ApiResponse<T = any> {
    code: number;
    data: T;
    message?: string;
}

// 创建 axios 实例
const request: AxiosInstance = axios.create({
    baseURL: import.meta.env.VITE_API_BASE_URL_V1 || '', // 使用正确的环境变量名称
    timeout: 10000, // 10秒超时
    headers: {
        'Content-Type': 'application/json',
        'Channel-Code': '2kuNGQpqrrK',
    },
});

// 请求拦截器
request.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        // 添加 Token 到请求头
        const token = localStorage.getItem('token');
        if (token && config.headers) {
            config.headers.Token = token;
        }
        return config;
    },
    (error: AxiosError) => {
        console.error('请求错误:', error);
        return Promise.reject(error);
    }
);

// 响应拦截器
request.interceptors.response.use(
    (response: AxiosResponse<ApiResponse>) => {
        const { data } = response;

        // 检查业务状态码
        if (data.code === 0) {
            // 1004 在现有逻辑上需要因为需要根据这个有没有卡来判断是否开卡
            // 成功响应，返回数据
            return data.data;
        } else if (data.code === 1004) {
            // 没有卡片
            return data;
        } else if (data.code === 1001) {
            localStorage.removeItem('token');
            localStorage.removeItem('app_state');
            Toast.info({
                message: i18n.t('登录过期请重新登录'),
                duration: 1000,
                onClose: () => {
                    // 跳转到登录页面
                    window.location.href = '/login';
                }
            })
            return Promise.reject(new Error('Token Error'));
        } else {
            Toast.info({
                position: 'top',
                message: data.message || `errorCode: ${data.code}`,
            })
            // 其他 code 码，打印错误信息
            console.error(`API错误 [${data.code}]:`, data.message || '未知错误');
            return Promise.reject(new Error(data.message || `${data.code}`));
        }
    },
    (error: AxiosError) => {
        // 处理网络错误、超时等
        if (error.code === 'ECONNABORTED') {
            // 请求超时
            Toast.info({
                position: 'top',
                message: i18n.t('请求超时请稍后重试'),
            })
            console.error('请求超时');
        } else if (error.response) {
            // 服务器响应了错误状态码
            const { data } = error.response as AxiosResponse<ApiResponse>;
            Toast.info({
                position: 'top',
                message: data.message! || `errorCode: ${data.code!}`,
            })
        } else if (error.request) {
            // 请求已发出但没有收到响应
            console.error('网络错误，请检查网络连接');
            Toast.info({
                position: 'top',
                message: `Network Error`,
            })
        } else {
            // 其他错误
            console.error('请求配置错误:', error.message);
        }

        return Promise.reject(error);
    }
);

// 封装常用的请求方法
export const api = {
    get: <T = any>(url: string, config?: InternalAxiosRequestConfig): Promise<T> => {
        return request.get(url, config);
    },

    post: <T = any>(url: string, data?: any, config?: InternalAxiosRequestConfig): Promise<T> => {
        return request.post(url, data, config);
    },

    put: <T = any>(url: string, data?: any, config?: InternalAxiosRequestConfig): Promise<T> => {
        return request.put(url, data, config);
    },

    delete: <T = any>(url: string, config?: InternalAxiosRequestConfig): Promise<T> => {
        return request.delete(url, config);
    },

    patch: <T = any>(url: string, data?: any, config?: InternalAxiosRequestConfig): Promise<T> => {
        return request.patch(url, data, config);
    }
};

// 导出 axios 实例（如果需要更复杂的配置）
export default request;

// 导出类型定义
export type { ApiResponse, InternalAxiosRequestConfig, AxiosResponse };